// Copyright (c) 2022 by Move Closer

import { Component, Inject, Mixins, Prop, Watch } from 'vue-property-decorator'
import merge from 'lodash/merge'

import { AnyModule, CanBeAlignedInGrid, isValidEnumValue } from '../../../contexts'
import { defaultProvider, IS_MOBILE_PROVIDER_KEY, logger } from '../../../support'
import { StructureConfigurable } from '../../../support/mixins'

import { Enum } from '../../utility-types'
import { UseListeners } from '../../mixins/UseListeners'

import { AbstractModule } from '../abstract-module'
import {
  DEFAULT_MODULE_HORIZONTAL_ALIGNMENT,
  DEFAULT_MODULE_VERTICAL_ALIGNMENT,
  horizontalAlignmentJustifySelfValueRegistry,
  verticalAlignmentAlignSelfValueRegistry
} from './abstract-module.ui.config'

/**
 * The abstract class that every module's UI container component **HAS TO** extend.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@Component<AbstractModuleUi<AnyModule>>({
  name: 'AbstractModuleUi',
  mounted (): void {
    if (!this.isVisible) {
      this.$emit('decorate', '--with-overlay')
    }
  }
})
export class AbstractModuleUi<Module extends AnyModule> extends Mixins(AbstractModule, StructureConfigurable, UseListeners) {
  /**
   * Component's alignment within the grid.
   *
   * In fact, it will be the component's alignment within its parent HTML element,
   * which is also a grid container, but don't tell anyone ;)
   * It's a workaround, I know, but we wanted to keep the `<Viewer>` and `<Module>` components
   * as sterile and agnostic as possible. Ideally, this data would've been
   * passed up in the tree to the `<Module>` component which is the REAL grid item.
   * This would cost us development time and make the `<Module>` component more complicated.
   * We decided to go another way, which is a bit less elegant, but should produce similar results.
   */
  @Prop({ required: false })
  private readonly alignment?: CanBeAlignedInGrid['alignment']

  @Prop({ type: Object, required: true })
  public readonly data!: Module

  @Inject({ from: 'isBackoffice', default: false })
  private readonly isBackoffice!: boolean

  @Inject({ from: IS_MOBILE_PROVIDER_KEY, default: () => defaultProvider<boolean>(false) })
  protected readonly isMobile!: () => boolean

  /**
   * Enum describing the possible values for `this.data.version`.
   */
  protected versionEnum?: Enum
  protected defaultContent: Module['content'] | null = null

  /**
   * Module content.
   */
  public get content (): Module['content'] {
    if (this.defaultContent) {
      return merge({}, this.defaultContent, this.data.content)
    }
    return this.data.content
  }

  /**
   * Determines whether `this.data.content` is specified.
   */
  public get hasContent (): boolean {
    if (typeof this.data === 'undefined') {
      logger('[AbstractModuleUi]: this.data is [undefined]!', 'error')
      return false
    }

    if (typeof this.data.content === 'undefined') {
      logger('[AbstractModuleUi]: this.data.content is [undefined]!', 'error')
      return false
    }

    return true
  }

  /**
   * Determines whether `this.data.version` is a valid value from `this.versionEnum` enum.
   */
  public get hasValidVersion (): boolean {
    if (typeof this.data.version === 'undefined') {
      return false
    }

    if (typeof this.versionEnum === 'undefined') {
      logger(
        '[AbstractModule]: this.versionEnum is (undefined)! You probably forgot to override this property in the subclass.',
        'warn'
      )
      return false
    }

    return isValidEnumValue(this.data.version, this.versionEnum)
  }

  /**
   * Determines whether component is visible.
   */
  public get isVisible (): boolean {
    return this.data.isVisible
  }

  /**
   * Determines whether the component has all the data it needs for a successful render.
   */
  public get shouldRender (): boolean {
    return this.hasContent
  }

  /**
   * Value for the `:style` binding.
   */
  public get style (): { alignSelf: string; justifySelf: string } {
    const alignment = this.alignment ?? this.data.alignment
    return {
      alignSelf: verticalAlignmentAlignSelfValueRegistry[
        alignment?.y?.[this.isMobile() ? 'mobile' : 'desktop'] ?? DEFAULT_MODULE_VERTICAL_ALIGNMENT
      ],
      justifySelf: horizontalAlignmentJustifySelfValueRegistry[
        alignment?.x?.[this.isMobile() ? 'mobile' : 'desktop'] ?? DEFAULT_MODULE_HORIZONTAL_ALIGNMENT
      ]
    }
  }

  /**
   * Watch isVisible property and emits event to decorate parent element
   */
  @Watch('isVisible')
  private onVisibilityChange (isVisible: boolean) {
    this.$emit('decorate', isVisible ? '' : '--with-overlay')
  }
}
